import React from 'react'
import { useTranslation } from 'react-i18next'
import { PushpinFilled, PushpinOutlined } from '@ant-design/icons'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { TourHelpButton } from '@/components/Organizer/Tours'
import useTourStore from '@/components/Organizer/Tours/tourStore'
import OrgSideHead from './OrgSideHead'
import OrgMenuList from './OrgMenuList'

/**
 * The organizer side menu (mock `aside.osd`): scrim (≤ 1100), the round pin straddling the panel's
 * right edge (> 1100, sticky 0-height wrapper so the panel's own scrolling never clips it), then the
 * panel: identity head, groups. No Status block any more (William, 29 Oct 2026): each publish switch
 * lives on its page, the event page's above the banner. Visual state lives on `body` (see
 * useOrgSideState); ×, the scrim and a navigation fold the drawer and the hover overlay alike. `tour`
 * (organizers only) adds the « Guided tour » row under the groups; a playing tour may force a group open.
 */
export default function OrgSideMenu({ side, groups, base, active, event, setCompetition, tour }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const { asideRef, pinned, rail, unfolded, handlers, onRailClick, setPinned, fold } = side
    const forcedOpenKey = useTourStore((state) => state.menuHold?.group ?? null)

    const onClick = (event) => {
        if (onRailClick(event)) {
            event.stopPropagation()
        }
    }

    return (
        <aside
            ref={asideRef}
            className="osd"
            id="orgSide"
            data-tour="side-menu"
            aria-label={t('Organizer menu')}
            onClickCapture={onClick}
            {...handlers}
        >
            <button type="button" className="osd-scrim" tabIndex={-1} aria-hidden="true" onClick={fold} />
            <div className="osd-pinw">
                <button
                    type="button"
                    className="osd-pin"
                    data-tour="menu-pin"
                    aria-pressed={pinned}
                    aria-label={pinned ? t('Unpin the menu') : t('Pin the menu')}
                    onClick={() => setPinned(!pinned)}
                >
                    {pinned ? <PushpinFilled /> : <PushpinOutlined />}
                </button>
            </div>
            <nav className="osd-panel" aria-labelledby="osdTitle">
                <OrgSideHead name={event?.name} iconLink={event?.iconLink} onClose={fold} />
                <OrgMenuList
                    groups={groups}
                    base={base}
                    activeEntry={active.entry}
                    activeGroup={active.group}
                    unfolded={unfolded}
                    forcedOpenKey={forcedOpenKey}
                    competition={event}
                    setCompetition={setCompetition}
                    onNavigate={fold}
                />
                {tour && <TourHelpButton onBeforeStart={fold} />}
            </nav>
        </aside>
    )
}
